import { createStore } from 'vuex'
import axios from 'axios'
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import router from './router';
import lodash from 'lodash';

// Redeclaring the API URL so we can use it in this file without having to eslint for eternity. It is originally declared in the index.html file and passed in on build.
// eslint-disable-next-line
const LOCAL_API_BASE_URI = process.env.VUE_APP_APIURL + '/';


const getDefaultState = () => {
  return {
    authInfo: {
      permissions: [],
    },
    chrome: {
      getprimaryNav: [],
    },
    breadcrumbs: {},
    currentEntityLoaded: false,
    itemsBeingLoaded: [],
    currentFetch: [],
    documents: {},
    entity: {},
    pageLoads: {},
    appNotice: {
      message: "",
      show: false,
      color: null,
      timeout: 5000,
    },
    lambdaLogs: [],
    sourceOfTruth: {
      owner: {},
      contractor: {},
      owner_contact: {},
      contractor_contact: {},
      location: {},
      unit_price_book: {},
    },
  };
};

const store = createStore({
  state() {
    return getDefaultState();
  },
  getters: {
    getprimaryNav: (state) => {
      return state.chrome.primaryNav;
    },
    getBreadcrumbsV2: (state) => (routePath) => {
      return state.breadcrumbs[routePath] || [];
    },
    getEntityDocument: (state) => (entityID) => {
      const entity = state.documents?.[entityID] || null;
      return entity;
    },
    getEntityCore: (state) => (entityID) => {
      const entity = state.documents?.[entityID]?.core || {};
      return entity;
    },
    getChildList: (state) => (queryPath, queryType) => {
      const documents = state.documents;
      const documentList = Object.values(documents).filter(doc => 
        doc._path.includes(queryPath) &&
        doc._type === queryType
      );
      documentList.sort((a, b) => {
          if (a._created.timestamp < b._created.timestamp) return -1;
          if (a._created.timestamp > b._created.timestamp) return 1;
          return 0;
      });
      return documentList;
    },
    getChildListFromIds: (state) => (queryPath, queryType, filterArray = []) => {
      const documents = state.documents;
      const documentList = Object.values(documents).filter(doc => 
        doc._path.includes(queryPath) &&
        doc._type === queryType &&
        filterArray.includes(doc._id)
      );
      documentList.sort((a, b) => {
          if (a._created.timestamp < b._created.timestamp) return -1;
          if (a._created.timestamp > b._created.timestamp) return 1;
          return 0;
      });
      return documentList;
    },
    getContractorList: (state) => (ownerID) => {
      const documents = state.documents;
      const documentList = Object.values(documents).filter(doc => {
        if (doc._type === "contractor") {
          return doc?.core?.associated_owners.includes(ownerID);
        }
        return false;
      });
      documentList.sort((a, b) => {
        if (a._created.timestamp < b._created.timestamp) return -1;
        if (a._created.timestamp > b._created.timestamp) return 1;
        return 0;
    });
      return documentList;
    },
    isCurrentEntityLoaded: (state) => {
      // console.log("currentEntityLoaded", state.currentEntityLoaded);
      return state.currentEntityLoaded;
    },
    /**
     * Get the top level entity objects for a given entityType
     * @param {string} entityType
     * @returns {object} - The top level entity objects for the given entityType
     */
    getTopLevelEntityObjects: (state) => (entityType) => {
      return state?.sourceOfTruth?.[entityType];
    },
    /**
     * Get the top level entity objects for a given entityType where the parent_id matches the parentID
     * @param {string} entityType
     * @param {string} parentID
     * @returns {object} - The top level entity objects for the given entityType after it is filtered by the parentID
     */
    getTopLevelEntityObjectsByParentID: (state) => (entityType, parentID) => {
      let entities = state?.sourceOfTruth?.[entityType];
      const filteredResults = {};
      if (entities) {
        Object.keys(entities).forEach((key) => {
          const item = entities[key];
          if (item.parent_id === parentID) {
            filteredResults[key] = item;
          }
        });
      }
      return filteredResults;
    },
    getEntityByID: (state) => (ID) => {
      return state?.entity?.[ID] || {};
    },
    getItemsBeingLoaded: (state) => {
      return state.itemsBeingLoaded;
    },
    getCurrentFetch: (state) => {
      return state.currentFetch;
    },
    getAppNotice: (state) => {
      return state.appNotice;
    },
    getAccesstoken: (state) => {
      return state.authInfo.accessToken;
    },
    getUserPermissions: (state) => {
      return state.authInfo.permissions;
    },
    getAuthInfo: (state) => (item) => {
      return state.authInfo[item];
    },
    getAPIheaders: (state) => {
      return {
        "Content-Type": "application/json",
        Authorization: state.authInfo.accessToken,
      };
    },
    getModelLoaded: (state) => ( path ) =>{
      console.log(path);
      return state.pageLoads.all;
    }
  },
  actions: {
    setBreadcrumbsV2(context, value) {
      context.commit("CONVERT_TO_BREADCRUMBS", value);
      // context.state.breadcrumbs[value.fullPath] = value.array;
    },
    setCurrentEntityLoaded(context, value) {
      context.state.currentEntityLoaded = value;
    },
    async pdfGenerator(context, payload){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}docgen/create/${payload.templateType}/${payload.entityId}`; 
      return await axios
        .put(fetchURL, payload, headers)
        .then(async function (response) {
          console.log("pdfGenerator",response.data);
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },
    async fetchFileListDataVersionTwo(context, fetchRequest) {
      const headers = await store.dispatch("generateAPIHeaders");
      let items = null;
      let fetchURL = LOCAL_API_BASE_URI + `docgen/list/${fetchRequest.templateType}/${fetchRequest.entityPath}`
      await axios.get(fetchURL, headers)
      .then(async function (response) {
        // console.log("fetchFileListDataVersionTwo",response.data.requestResponse.Contents || []);
        items = response.data.requestResponse.Contents || null;
      }).catch(function(catchError){ 
        throw new Error(catchError);
      })
      return items;
    },

    
    /* #### Cognito User Management #### */

    async getUserPermissionsList(context) {
      const fetchURL = LOCAL_API_BASE_URI + "user/permissionsList";
      const headers = await store.dispatch("generateAPIHeaders");
      await axios.get(fetchURL, headers)
      .then(async function (response) {
        context.commit("SET_USER_INFO", {
          permissions: response.data,
        });
        return;
      }).catch(error => {
        console.warn(error.response.data.errorMessage);
      });
    },
    
    async getUserChrome(context) {
      const fetchURL = LOCAL_API_BASE_URI + "V2/userChrome";
      const headers = await store.dispatch("generateAPIHeaders");
      await axios.get(fetchURL, headers)
      .then(async function (response) {
        context.commit("ADD_CHROME_TO_STORE", response.data.data);
        return;
      }).catch(error => {
        console.warn(error.response.data.errorMessage);
      });
    },

    async getUserStatus(context, entityId){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}user/status/${entityId}`; 
      return await axios
        .get(fetchURL, headers)
        .then(async function (response) {
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },

    async getOwnerContact(context, entityId){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}owner-contact/${entityId}`; 
      return await axios
        .get(fetchURL, headers)
        .then(async function (response) {
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },
    async getProjects(){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}projects`; 
      return await axios
        .get(fetchURL, headers)
        .then(async function (response) {
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },

    async changeUserStatus(context, entityId){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}user/status/${entityId}`;
      return await axios
      .put(fetchURL, null, headers)
        .then(async function (response) {
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },



    async entityGlobalRead(context, request){
      if(request.currentEntity){
        context.dispatch("setCurrentEntityLoaded", false);
      }
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}${request.entityType}/${request.entityID}`; 
      if (context.state.currentFetch.includes(request.entityID)) {
        return;
      }
      context.state.currentFetch.push(request.entityID);
      return await axios
        .get(fetchURL, headers)
        .then(async function (response) {
          // console.log("response.data",response.data);
          await context.commit("ADD_DOCUMENT_TO_STORE", response.data);
          context.state.currentFetch = context.state.currentFetch.filter(id => id !== request.entityID);
          if(request.currentEntity){
            context.dispatch("setCurrentEntityLoaded", true);
          }
        })
        .catch(error => {
          // console.log('error.response.data',error.response.data)
          console.warn(error.response);
          console.log('request',request)
        });
    },
    async entityGlobalList(context, request){
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}list/${request.entityType}/${request.lookupValue}`; 
      if (context.state.currentFetch.includes(request.lookupValue)) {
        return;
      }
      context.state.currentFetch.push(request.entityType+"."+request.lookupValue);
      return await axios
        .get(fetchURL, headers)
        .then(async function (response) {
          // console.log("response.data",response.data)
          await context.commit("ADD_DOCUMENT_TO_STORE", response.data);
          context.state.currentFetch = context.state.currentFetch.filter(id => id !== request.lookupValue);
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },
    async entityGlobalCreateWithoutRoute(context, payload){
      console.log("payload1",payload)
      const entityTypeToCreate = payload.routing.entityTypeToCreate;
      const parentId = payload.routing.parentId;
      delete payload.routing;
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}${entityTypeToCreate}/${parentId}`; 
      return await axios
        .post(fetchURL, payload, headers)
        .then(async function (response) {
          console.log("response",response.data);
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },
    async entityGlobalUpdateWithoutRoute(context, payload){
      console.log("payload2",payload)
      const entityTypeToUpdate = payload.routing.entityTypeToUpdate;
      const entityId = payload.routing.entityId;
      delete payload.routing;
      if (entityTypeToUpdate === 'proposal' || entityTypeToUpdate === 'supplemental_proposal') {
        const allowedKeys = ['name', 'date_submitted', 'status', 'unit_price_book_uid', 'subcontractors', 'external_estimates'];
        const core = payload.payload;
        const sanitizedCore = {};
        for (const key of allowedKeys) {
          if (Object.prototype.hasOwnProperty.call(core, key)) {
            if (key === 'date_submitted') {
              const v = core[key];
              if (v == null || v === '') {
                sanitizedCore[key] = null;
              } else if (v instanceof Date) {
                sanitizedCore[key] = v.toISOString();
              } else if (typeof v === 'string') {
                const d = new Date(v);
                sanitizedCore[key] = isNaN(d.getTime()) ? null : d.toISOString();
              } else {
                sanitizedCore[key] = v;
              }
            } else {
              sanitizedCore[key] = core[key];
            }
          }
        }
        payload.payload = sanitizedCore;
      }
      const headers = await store.dispatch("generateAPIHeaders");
      const fetchURL = `${LOCAL_API_BASE_URI}${entityTypeToUpdate}/${entityId}`; 
      const entityDoc = context.getters.getEntityDocument(entityId);
      payload.updated = entityDoc.updated;
      return await axios
        .put(fetchURL, payload, headers)
        .then(async function (response) {
          console.log("response",response.data);
          return response.data;
        })
        .catch(error => {
          console.warn(error.response.data.errorMessage);
        });
    },



    async getElementByID(context, id) {
      if(id == null){
        return null;
      }

      let final;
      
      let entity = context.getters.getEntityByID(id);
      
      if (JSON.stringify(entity) != "{}" && entity.name !== null) {
        return entity;
      } else {
        let fetchURL = `${LOCAL_API_BASE_URI}item/${id}`; 

        let loadingID = Math.random().toString(36).substring(2) + Date.now().toString(36);
        context.commit("WAITING_FOR_DATA", loadingID);
        const headers = await store.dispatch("generateAPIHeaders");
        return await axios
          .get(fetchURL, headers)
          .then(async function (response) {
            final = response.data.payload;

            let entity = {      
              id: final.entity._id,    
              display: final.entity.name,
              type: final.entityType,
              entityObj: final.entity,
              path: final.rootPath,
              navPath: final.navPath,
            }
            await context.commit("UPDATE_PATH_BY_ID", entity);
            await context.commit("DONE_WAITING_FOR_DATA", loadingID);
            
            return entity;
          });
      }

    },
    async generateCalulations(context, payload) {
      if(payload == null){
        return null;
      }
      let fetchURL = `${LOCAL_API_BASE_URI}calculation/totals`; 
      const headers = await store.dispatch("generateAPIHeaders");
      return await axios
        .post(fetchURL, {"id":payload.entityID}, headers)
        .then(async function (response) {
          const calculations = response.data;
          console.log("calculations",calculations)
        });
    },
    async checkProposalFormat(context, payload) {
      if(payload == null){
        return null;
      }
      let fetchURL = `${LOCAL_API_BASE_URI}calculation/totals/return`; 
      const headers = await store.dispatch("generateAPIHeaders");
      return await axios
        .post(fetchURL, payload, headers)
        .then(async function (response) {
          return response.data.success;
        });
    },
    async parseSourceofTruth(context) {
      const sourceOfTruth = context.state.sourceOfTruth;
      await context.dispatch('recurseNodes', { payload: sourceOfTruth, path: "", ancestry:[]});

      await context.dispatch('loopTypes', { type: 'contact', entity: sourceOfTruth.contractor_contact } );
      await context.dispatch('loopTypes', { type: 'contact', entity: sourceOfTruth.owner_contact } );
      await context.dispatch('loopTypes', { type: 'location', entity: sourceOfTruth.location } );
      await context.dispatch('loopTypes', { type: 'unit_price_book', entity: sourceOfTruth.unit_price_book } );
    },
    async appGetListData(context, entity) {
      const headers = await store.dispatch("generateAPIHeaders");
      let entityObj = {};
      
      // Used to hot-swap between which Lambda is being targeted
      let fetchURL = `${LOCAL_API_BASE_URI}${entity.relatedType}/list`; 

      let payload = {
        parentID: entity.parentID,
        parentType: entity.parentType,
      };

      let loadingID = Math.random().toString(36).substring(2) + Date.now().toString(36);

      let l = await axios
        .post(fetchURL, payload, headers)
        .then(async function (response) {
          if(!response.data.payload.list){
            return;
          }

          response.data.payload.list.map((item) => {  
            item.id   = item._id;
            item.key  = item._id;
            entityObj[item._id] = item;
          });

          let payload = response.data.payload;
          
          //set each item individually to the dom
          payload.list.forEach((item) => {
            
            let name;
            if(item.first_name)
              name = `${item.first_name} ${item.last_name}`;
            else if (item.name)
              name = `${item.name}`;
            else if (item.display)
              name = `${item.display}`;
            else
              name = `${item.type}`;

            let entity = {      
              id: item.id,    
              display: name,
              type: payload.type,
              entityObj: item,
              path: `${payload.path}.${item.id}`,
              navPath: `${payload.navpath}.${item.id}`,
            }

            context.commit("UPDATE_PATH_BY_ID", entity);
          })

          //eslint-disable-next-line
          let nodes = {
            parentID: entity.parentID,
            type: entity.relatedType,
            targetType: entity.relatedType,
            path: response.data.payload?.path,
            list: entityObj
          }
          
          await context.commit("ADD_LIST_TO_STORE", nodes);

          context.commit("ADD_APP_INFO", response.data.appInfo);
          return entityObj;
        })
        .catch(function (catchError) {
          throw new Error(catchError);
        });

      context.commit("DONE_WAITING_FOR_APPDATA", loadingID);
      
      return l;
    },   
    async appGetPageData(context, item) {
      let entityID;
      let vueBus;
      
      if(item == null){
        return 
      } else {
        entityID = item.entityID;
        vueBus = item.vueBus;
       }

       const headers = await store.dispatch("generateAPIHeaders");
      let fetchURL = `${LOCAL_API_BASE_URI}entity/page`; 
      let nodes;
      let payload = {
        entityID: entityID
      };
      let loadingID = Math.random().toString(36).substring(2) + Date.now().toString(36);
      context.commit("WAITING_FOR_DATA", loadingID);
      
      await axios
        .post(fetchURL, payload, headers)
        .then(async function (response) {
          
          nodes = response.data.payload

          await context.commit("ADD_INIT_TO_STORE", nodes);
          await context.dispatch("parseSourceofTruth");

          await context.commit("ADD_APP_INFO", response.data.appInfo);
        })
        .catch(function (catchError) {
          throw new Error(catchError);
        });

      context.commit("DONE_WAITING_FOR_DATA", loadingID);
      context.commit("DONE_WAITING_FOR_APPDATA", loadingID);
      
      vueBus.emit("FINISHED_LOAD_APPDATA"); // SUKER look here for the BUS
      return nodes;
    },
    async loopTypes(context, payload){

      Object.values(payload.entity).forEach(async (item) => {

        let parent = context.state.entity[item.parent_id];
        console.log(parent)

        if(parent){
          let navPath = `${parent.path}.${payload.type}.${item._id}`;
          await context.commit("UPDATE_NAVPATH_BY_ID", {id: item._id, navPath: navPath});  
        }
      })
    },
    async recurseNodes( context, node){

      //each recursion requires two passes
      let node_list = node.payload;
      let ancestry = node.ancestry;

      //first pass is the object type
      for(let node_type in node_list){

        //is it an object, yes, let's do this, no? let's skip
        if(Object.prototype.toString.call(node_list[node_type]) === '[object Object]'){

          //this loop will be through child level IDs
          for(let child_id in node_list[node_type]){

            let child_item = node_list[node_type][child_id];
            let object_path = `${node.path}.${node_type}.${child_id}`
            let type_name = await context.dispatch('getName', child_item);

            switch (node_type) {
              case "address":
              case "metadata":
                continue;
              default:
                await context.commit("UPDATE_PATH_BY_ID", {
                  type: node_type, 
                  id: child_id, 
                  path: object_path.substring(1), 
                  entityObj: child_item, 
                  display: type_name,
                  //name: type_name
                });
            }


            //loop child_item to see what needs recursion
            for(let props in child_item){

              //check if object AND has an _id
              if(Object.prototype.toString.call(child_item[props]) == '[object Object]' ){
                let base_ancestry = [];
                if( !(
                    node_type == "metadata" &&
                    node_type == "address" &&
                    node_type == "config" )
                ){
                    //clone the ancestry
                    base_ancestry = JSON.parse(JSON.stringify(ancestry));

                    //add current node to excluded it in the ancestry, move it below the next line
                    base_ancestry.push({entity_type: node_type, entity_id: child_id, name: type_name})

                    //grip it and rip it.
                    await context.dispatch('recurseNodes', { payload: child_item, path: object_path, ancestry: base_ancestry});
                }
              }
            }
          }
        }
      }
    },
    getName(context, entity){
      let type_name = "";
      if(entity && entity.name ){
        type_name = entity.name
      } else if (entity && entity.first_name){
        type_name = `${entity.first_name} ${entity.last_name}`
      }
      return type_name;
    },
    async logUserOut(context) {
      await signOut();
      router.push({ path: "/" });
      context.commit("DELETE_STATE");
    },
    /**
     *
     * @param {object} submitData.lookupInfo - Provides all of the context needed to make the request.
     * @param {object} submitData.payload - The payload object to send to the API. This is the actual data that we want to add or update.
     * @returns returnID - The ID of the entity that was created
     */
    
    async appEntitySaveAndNav(context, submitData){
      let id = await context.dispatch("appEntitySave", submitData);
      submitData.navID = id;
      await context.dispatch("appEntityNav", submitData);
    },

    async appEntityNav(context, submitData){
      let props = {
        path: "/" + submitData.lookupInfo.entityType + "/" + submitData.navID + "/read",
      };
      router.push(props);

    },
    async getSelectedProposal(context, work_order_id){
      let param = {
        parentID: work_order_id,
        parentType: "work_order",
        relatedType: "proposal",
      };

      let list = await store.dispatch("appGetListData", param);
      
      for(var item in list){
        console.log(list[item])
        if(list[item].status=="approved"){
          return list[item]
        }
      }
      return null;
    },

    async appEntitySave(context, submitData) {
      let returnID = null;

      console.log("submitData", submitData);

      const headers = await store.dispatch("generateAPIHeaders");
      let requestType = submitData.lookupInfo.requestType;
      let endpoint = LOCAL_API_BASE_URI + "entity/" + requestType + "FromSnap";

      // Generate unique loading ID: random string + timestamp for collision resistance
      let loadingID =
        Math.random().toString(36).substring(2) + Date.now().toString(36);
      context.commit("WAITING_FOR_DATA", loadingID);

      await axios
        .post(endpoint, submitData, headers)
        .then(async function (response) {
          console.log("response", response);
          context.commit("ADD_APP_INFO", response.data.appInfo);
          if (response.data.appInfo.success) {
            await context.commit("MERGE_NEW_DATA", response.data.payload);
            
            
            await context.dispatch("parseSourceofTruth");
            returnID = response.data.appInfo.returnID;

          } else {
            // If there is a failed MongoDB validation, the error message gets returned in the response payload now. 
            // Using the if statement here, it can be determined that the error message came through.
            // This parses through the error message and creates a snackbar message that lists out the reasons for failures
            // The reasons for failures are the descriptions of each property that failed validation
            if (response.data.payload?.errInfo?.details?.schemaRulesNotSatisfied[0]?.propertiesNotSatisfied){
              const responseArray = response.data.payload.errInfo.details.schemaRulesNotSatisfied[0].propertiesNotSatisfied;

              let notice = 'Document Validation Failed:';
              responseArray.forEach((entry) => {
                notice = notice.concat(`<br> - ${entry.description}`);
              });
              const appNotice ={
                message: notice,
                success: false
              }
              store.dispatch('createAppNotice', appNotice);
            }
          }
        })
        .catch(function (catchError) {
          let errorNotice = {
            primaryMessage: catchError,
            success: false,
          };
          context.commit("ADD_APP_INFO", errorNotice);
          throw new Error(catchError);
        });
      context.commit("DONE_WAITING_FOR_DATA", loadingID);
      return returnID;
    },

    async appUserCreate(context, payload) {
      const headers = await store.dispatch("generateAPIHeaders");

      let apiFullUri = LOCAL_API_BASE_URI + "user/create";
      let loadingID =
        Math.random().toString(36).substring(2) + Date.now().toString(36);
      context.commit("WAITING_FOR_DATA", loadingID);
      await axios
        .post(apiFullUri, payload, headers)
        .then(async function (response) {
          context.commit("ADD_APP_INFO", response.data.appInfo);
          if (response.data.appInfo.success) {
            context.commit("SPREAD_ENTITY_TO_STATE", response.data.payload);
          }
        })
        .catch(function (catchError) {
          let errorNotice = {
            primaryMessage: catchError,
            success: false,
          };
          context.commit("ADD_APP_INFO", errorNotice);
          throw new Error(catchError);
        });
      context.commit("DONE_WAITING_FOR_DATA", loadingID);
    },
    async appUserDemote(context, payload) {
      const headers = await store.dispatch("generateAPIHeaders");

      let apiFullUri = LOCAL_API_BASE_URI + "user/demote";
      let loadingID =
        Math.random().toString(36).substring(2) + Date.now().toString(36);
      context.commit("WAITING_FOR_DATA", loadingID);
      await axios
        .post(apiFullUri, payload, headers)
        .then(async function (response) {
          context.commit("ADD_APP_INFO", response.data.appInfo);
          if (response.data.appInfo.success) {
            context.commit("SPREAD_ENTITY_TO_STATE", response.data.payload);
          }
        })
        .catch(function (catchError) {
          let errorNotice = {
            primaryMessage: catchError,
            success: false,
          };
          context.commit("ADD_APP_INFO", errorNotice);
          throw new Error(catchError);
        });
      context.commit("DONE_WAITING_FOR_DATA", loadingID);
    },
    async appUserPromote(context, payload) {
      const headers = await store.dispatch("generateAPIHeaders");

      let apiFullUri = LOCAL_API_BASE_URI + "user/promote";
      let loadingID =
        Math.random().toString(36).substring(2) + Date.now().toString(36);
      context.commit("WAITING_FOR_DATA", loadingID);
      await axios
        .post(apiFullUri, payload, headers)
        .then(async function (response) {
          context.commit("ADD_APP_INFO", response.data.appInfo);
          if (response.data.appInfo.success) {
            context.commit("SPREAD_ENTITY_TO_STATE", response.data.payload);
          }
        })
        .catch(function (catchError) {
          let errorNotice = {
            primaryMessage: catchError,
            success: false,
          };
          context.commit("ADD_APP_INFO", errorNotice);
          throw new Error(catchError);
        });
      context.commit("DONE_WAITING_FOR_DATA", loadingID);
    },

    async fetchDownload(context, fetchRequest) {
      const headers = await store.dispatch("generateAPIHeaders");
      let fetchURL = LOCAL_API_BASE_URI + "docgen/download"
      
      await axios.post(fetchURL, fetchRequest, headers)
      .then(async function (response) {
        // Get the data from the response
        console.log(response)

        window.open(response.data.message)
        console.log(response)
      }).catch(function(catchError){
        console.log(catchError);
        throw new Error(catchError);
      });
    },

    async deleteDocument(context, documentKey){
      const headers = await store.dispatch("generateAPIHeaders");
      const encodedURL = encodeURIComponent(documentKey);
      const fetchURL = `${LOCAL_API_BASE_URI}docgen/delete?file=${encodedURL}`;
      return await axios
        .delete(fetchURL, headers)
        .then(async function () {
          return true;
        })
        .catch(error => {
          console.log("pdfGenerator",error.response.data);
          console.warn(error.response.data.errorMessage);
        });
    },
    async postFile(context, fetchRequest) {
      var headers = {
        'Content-Type': 'text/plain',
        'Authorization': store.getters.getAccesstoken
      };

      return await axios.put(fetchRequest.url, fetchRequest.file, headers)
      .then(async function (response) {
        // Get the data from the response
        if(response.data.success){
          return response.data.message;
        }
      }).catch(function(catchError){ 
        console.log(catchError)
        throw new Error(catchError);
      })
    },

    

    async isUserLoggedIn() {
      try{
        await getCurrentUser();
        return true;
      }catch(error){
        return false
      }
    },

    async isIdTokenValid(context){
      const tokenExpiration = context.state.authInfo.idToken.exp;
      const checkAgainstTime = Math.floor(new Date().getTime() / 1000) + 30;
      return tokenExpiration > checkAgainstTime ? true : false;
    },

    async fetchAndStoreIdToken(context) {
      try {
        const authSession = await fetchAuthSession();
        context.commit("SET_USER_INFO", {
          authJWT: authSession.tokens.idToken.toString(),
          idToken: authSession.tokens.idToken.payload,
        });
        const userGroups = authSession.tokens.idToken.payload["cognito:groups"];
        if(!userGroups){
          context.dispatch("logUserOut");
        }
        if(userGroups.includes("owner_project_manager")){
          return false;
        }
        return true;
      } catch (error) {
        throw new Error(error);
      }
    },

    async setupUser(context){
      const shouldGetPermissions = await context.dispatch("fetchAndStoreIdToken");
      await context.dispatch("getUserChrome");
      if(shouldGetPermissions){
        await context.dispatch("getUserPermissionsList");
        return '/owners';
      }
      return '/opm/projects';
    },

    // async generateAPIHeaders(context){
    //   if(!await context.dispatch("isIdTokenValid")){
    //     await context.dispatch("fetchAndStoreIdToken");
    //   }
    //   return {
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'Authorization': context.state.authInfo.authJWT
    //     }
    //   };
    // },
    async getAuthJwt(){
        const authSession = await fetchAuthSession();
        return authSession.tokens.idToken.toString();
    },
    async generateAPIHeaders(context){
        const jwt = await context.dispatch('getAuthJwt')
        return {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': jwt
            }
        };
    },
    
    createAppNotice(context, notice) {
      context.commit("CREATE_APP_NOTICE", notice);
    },
    async logBugToDB(context, payload) {
      const headers = await store.dispatch("generateAPIHeaders");
      let apiFullUri = LOCAL_API_BASE_URI + "logbug";
      await axios
        .post(apiFullUri, payload, headers)
        .then(async function (response) {
          context.commit("ADD_APP_INFO", response.data.appInfo);
        })
        .catch(function (catchError) {
          let errorNotice = {
            primaryMessage: catchError,
            success: false,
          };

          context.commit("ADD_APP_INFO", errorNotice);
          throw new Error(catchError);
        });
    },
  },
  mutations: {
    ADD_CHROME_TO_STORE(state, payload) {
      state.chrome = payload;
    },
    CONVERT_TO_BREADCRUMBS(state, payload) {
      // Using the shallow copies to avoid mutating the original data
      const items = [...payload.array]; // Create a shallow copy of the array
      items.forEach((item, index) => {
        items[index] = { ...item }; // Create a shallow copy of each item
        items[index].href = `/#${item.href}`;
        if (item.type) {
          items[index].title = `<span class="entity-type">${item.type}</span>${item.title}`;
        }
      });
      state.breadcrumbs[payload.fullPath] = items;
    },
    async ADD_DOCUMENT_TO_STORE(state, payload) {
      if(!payload.data){
        throw new Error("No data returned from API.");
      }
      payload.data.forEach(document => {
        const documentID = document._id;
        const segments = document._path.split('.');
        const entityType = segments[segments.length - 2];
        document._type = entityType;
        if(document?.core?.name){
          document._name = document?.core?.name;
        }else if(document?.core?.first_name){
          document._name = document?.core?.first_name + " " + document?.core?.last_name;
        }
        state.documents[documentID] = document;
      });
    },
    REMOVE_DOCUMENT_FROM_STORE(state, documentID) {
      if (state.documents[documentID]) {
        delete state.documents[documentID];
      }
    },
    ADD_LIST_TO_STORE(state, listData) {

      let element;
      switch(listData.type){
        //these are all top level entities
        case "contractor":
        case "unit_price_book":
        case "location":
        case "owner_contact":
        case "contractor_contact":
        case "owner":
          // Additive merge: preserve existing items, update/add new ones
          state.sourceOfTruth[listData.type] = {
            ...state.sourceOfTruth[listData.type],
            ...listData.list
          };
          break;
        default:
          element = this.getters.getEntityByID(listData.parentID);

          // Additive merge for nested entities
          if(Object.keys(element).length !== 0){
            element.entityObj[listData.type] = {
              ...element.entityObj[listData.type],
              ...listData.list
            };
          } else {
            let entityObj = {};
            entityObj[listData.targetType] = listData.list;
            
            //create parent object as entity in store
            let element = {
              type: listData.type, 
              parent_id: listData.parentID, 
              path: listData.path, 
              entityObj: entityObj,
              display: listData.type
            }

            //add entity to store
            state.entity[listData.parentID] = {};
            state.entity[listData.parentID][listData.type] = element;

          }
      }

    },
    ADD_INIT_TO_STORE(state, payload) {
      state.sourceOfTruth = payload;
    },
    MERGE_NEW_DATA(state, payload) {
      state.sourceOfTruth = lodash.merge({}, state.sourceOfTruth, payload);
    },
    ADD_APP_INFO(state, appInfo) {

      state.lambdaLogs.push(appInfo.lambdaLog);
      state.appNotice.message = appInfo.primaryMessage;
      state.appNotice.color = appInfo.success ? "green" : "red";
      state.appNotice.show = true;
      state.appNotice.timeout = appInfo.success ? 2000 : 5000;
      if (appInfo.catchError) {
        console.log("appInfo.catchError", appInfo.catchError);
      }
      if(appInfo.error){
        let errorMessage = "";
        const errors = appInfo.error.errInfo.details.schemaRulesNotSatisfied[0].propertiesNotSatisfied;
        errors.forEach((error) => {
          const details = error.details[0];
          const capitalized = error.propertyName.charAt(0).toUpperCase() + error.propertyName.slice(1);
          if(details.consideredType === "null" && details.consideredValue === null){
            errorMessage += `${capitalized} is a required field.<br/>`;
          }
          if(details.operatorName === "minLength"){
            errorMessage += `${capitalized} must be at least ${details.specifiedAs.minLength} characters.<br/>`;
          }
          if(details.operatorName === "maxLength"){
            errorMessage += `${capitalized} must be no longer than ${details.specifiedAs.maxLength} characters.<br/>`;
          }
          console.log("error",error);
        });
        state.appNotice.message = errorMessage;
      }
    },
    UPDATE_PATH_BY_ID(state, payload) {
      //thiis gets called on proposal over and over again, which is werid
      
      //building a gate to prevent the update race condition
      if(!state.entity?.type ){
        let entity = {
          name:       payload.name?payload.name:payload.display,
          type:       payload.type,
          entityObj:  payload.entityObj,
          path:       payload.path,
          navPath:    payload.path,
        };
        state.entity[payload.id] = entity  
      }

    },
    UPDATE_NAVPATH_BY_ID(state, payload){
      state.entity[payload.id].navPath = payload.navPath;
    },
    CREATE_APP_NOTICE(state, appNotice) {
      state.appNotice.message = appNotice.message;
      state.appNotice.color = appNotice.success ? "green" : "red";
      state.appNotice.show = true;
      state.appNotice.timeout = appNotice.success ? 2000 : 5000;
    },
    WAITING_FOR_DATA(state, loadingID) {
      state.itemsBeingLoaded.push(loadingID)
      return 'something'
    },
    WAITING_FOR_APPDATA(state, loadingID) {
      state.itemsBeingLoaded.push(loadingID)
      return 'something'
    },
    DONE_WAITING_FOR_APPDATA(state) {
      state.pageLoads.all = true;
    },
    DONE_WAITING_FOR_DATA(state, loadingID) {
      state.itemsBeingLoaded = state.itemsBeingLoaded.filter(
        (item) => item !== loadingID
      );
    },
    SET_USER_INFO(state, authObject) {
      state.authInfo = {
        ...state.authInfo,
        ...authObject
      };    
    },
    SET_EVENTBUS(state, eventBus) {
      state.eventBus = eventBus;
    },
    SET_APP(state, app) {
      state.app = app;
    },
    DELETE_STATE() {
      store.replaceState(getDefaultState());
    },
  },
});

export default store;